/**
 * Every string `/payment-gateways` renders.
 *
 * **No payment provider is named anywhere.** Printing a brand would assert a
 * commercial relationship no source in this workspace records; the registry's
 * names are strings the operator types, and the line above the table says so.
 *
 * **No credential appears in any string, in any form.** The name cell states
 * that a credential is stored; the rotation dialog replaces it; nothing reads it
 * back. That is the treatment a secret-classed configuration row already gets,
 * applied to the same class of value at a higher stake.
 */
export const copy = {
  crumb: 'بوّابات الدفع',

  head: {
    title: 'بوّابات الدفع',
    lede: 'سجلّ مزوّدي السداد: بيانات الاعتماد، ونقطة الاستدعاء، وحالة التفعيل، وتهيئة الرسوم، ومعاملة اختبارية لكلّ منها.',
  },

  /** The constraint, stated before the table it governs. A statement, not an error. */
  constraint: {
    title: 'بوّابة واحدة فاعلة في كلّ وقت',
    body: 'السداد مباشر: يسلّم فعل التأكيد في صفحة الدفع إلى البوّابة الفاعلة وحدها، ويُمنح الاستحقاق عند العودة بنجاح. تفعيلُ بوّابة يُخرج الأخرى من الخدمة في اللحظة نفسها، فلا رصيد يُحتجز ولا محفظة في المنتج.',
  },

  section: {
    heading: 'البوّابات المسجّلة',
    add: 'سجّل بوّابة',
  },

  namingNote:
    'اسم البوّابة حقلٌ تكتبه أنت لتمييز السجلّ، ولا يظهر لمشترك في أي صفحة. بيانات الاعتماد لا تُقرأ من أي سطح — تُستبدَل من قائمة الصفّ عبر «دوّر بيانات الاعتماد».',

  caption: 'بوّابات الدفع المسجّلة، بحالة كلّ منها ورسومها ونقطة استدعائها',

  columns: {
    gateway: 'البوّابة',
    state: 'الحالة',
    fee: 'الرسوم',
    callback: 'نقطة الاستدعاء',
    actions: 'أفعال البوّابة',
  },

  /** Under the gateway's name: a credential exists, and it is replaced rather than read. */
  credentialNote: 'بيانات الاعتماد محفوظة — تُدوَّر ولا تُقرأ',
  credentialMissingNote: 'لا مفتاح محفوظاً بعد — دوّر بيانات الاعتماد قبل التفعيل',

  state: {
    active: 'فاعلة',
    inactive: 'غير فاعلة',
  },

  rowMenu: {
    label: (name: string) => `أفعال «${name}»`,
    edit: 'حرّر البوّابة',
    rotate: 'دوّر بيانات الاعتماد',
    test: 'نفّذ معاملة اختبارية',
    activate: 'فعّل هذه البوّابة',
    delete: 'احذف البوّابة',
    /** Why two entries are disabled on the active row. The disabled control explains itself. */
    activeHint: 'هذه هي البوّابة الفاعلة: لا تُحذف ولا يُعاد تفعيلها. فعِّل بوّابة أخرى أولاً.',
  },

  feeNoteLead:
    'الرسم ما يقتطعه المزوّد من كلّ معاملة، ولا يراه مشترك: صفحة الدفع تعرض سعر الخطّة وحده. وما وصل فعلاً بعد الاقتطاع تقرأه من',
  feeNoteLink: 'تقارير الاشتراك',

  truncationTail: 'بوّابة واحدة منها فاعلة.',

  dialog: {
    createTitle: 'سجّل بوّابة',
    editTitle: 'حرّر البوّابة',
    context: 'سجلّ مزوّد سداد',

    nameLabel: 'اسم البوّابة',
    nameHint: 'اسمٌ يميّز السجلّ في هذا الجدول ولا يظهر لمشترك.',

    callbackLabel: 'نقطة الاستدعاء',
    callbackHint:
      'العنوان الذي يعود إليه المزوّد بنتيجة المعاملة. مسجَّل عند المزوّد أيضاً، والقيمتان تتطابقان أو لا تصل النتيجة.',

    feeLabel: 'الرسوم',
    feeHint: 'نسبة مئوية من قيمة المعاملة، كما يتعاقد عليها مع المزوّد.',
  },

  rotate: {
    title: 'دوّر بيانات الاعتماد',
    lede: 'المفتاح القديم يتوقّف عن العمل فور الحفظ، والمعاملات الجارية تُكمَل بالقديم. نفّذ معاملة اختبارية بعد التدوير للتحقّق.',
    storedLabel: 'القيمة المحفوظة',
    storedMask: '••••••••••••••••',
    storedHint:
      'مقنّعة دائماً — لا تُعرض من أي صفحة. إن فقدتَ المفتاح، أنشئ بديلاً من لوحة المزوّد وضعه في الحقل تحته.',
    newLabel: 'المفتاح الجديد',
    newHint: 'يُلصَق من لوحة المزوّد. لا يُعرض بعد الحفظ.',
    newRequired: 'ألصق المفتاح الجديد.',
    commit: 'دوّر المفتاح',
  },

  activate: {
    heading: 'تفعيل البوّابة؟',
    /** Naming only the incoming provider would hide that something left. */
    bodyWithOutgoing: (incoming: string, outgoing: string) =>
      `يتحوّل السداد إلى «${incoming}» فوراً، وتخرج «${outgoing}» من الخدمة في اللحظة نفسها. المعاملات الجارية تُكمَل بالبوّابة التي بدأتها.`,
    bodyFirst: (incoming: string) =>
      `يتحوّل السداد إلى «${incoming}» فوراً. لا بوّابة فاعلة اليوم، فهذه أوّل ما يمرّ به السداد.`,
    commit: 'فعّل البوّابة',
  },

  test: {
    title: 'معاملة اختبارية',
    lede: 'تُرسَل معاملة بأدنى مبلغ عبر هذه البوّابة ثمّ تُعكَس فوراً: لا استحقاق يُمنح، ولا سطر يدخل تقارير الاشتراك.',
    run: 'نفّذ المعاملة',
    running: 'جارٍ التنفيذ…',
    close: 'أغلق',
    successTitle: 'نجحت المعاملة الاختبارية',
    successBody:
      'وصلت النتيجة إلى نقطة الاستدعاء المسجّلة، وعُكست المعاملة فوراً. لا استحقاق مُنح، ولا سطر دخل تقارير الاشتراك.',
    failureTitle: 'لم تنجح المعاملة الاختبارية',
    failureBody: 'لم يقبل المزوّد المعاملة. راجع المفتاح ونقطة الاستدعاء ثمّ أعد التنفيذ.',
    lastTested: (date: string) => `آخر اختبار: ${date}`,
  },

  confirm: {
    heading: (name: string) => `حذف بوّابة «${name}» نهائياً؟`,
    body: 'يختفي سجلّ البوّابة ببياناته وبنقطة استدعائه. البوّابة الفاعلة لا تُحذف، فما يُحذف هنا لا يمرّ به سداد اليوم ولا معاملة جارية تنتظره.',
    commit: 'احذف البوّابة',
  },

  validation: {
    nameRequired: 'اكتب اسماً للبوّابة.',
    nameTooLong: 'الاسم أطول ممّا يقبله السجلّ.',
    callbackRequired: 'اكتب نقطة الاستدعاء.',
    callbackInvalid: 'اكتب عنواناً كاملاً يبدأ بـ‎https://‎.',
    feeRequired: 'اكتب نسبة الرسوم.',
    feeRange: 'النسبة بين صفر ومئة.',
  },

  states: {
    loading: 'جارٍ تحميل البوّابات',
    empty: 'لا بوّابة مسجّلة بعد. سجّل واحدة ثمّ دوّر مفتاحها ثمّ فعّلها.',
  },

  toast: {
    registered: 'سُجّلت البوّابة.',
    updated: 'حُفظت البوّابة.',
    deleted: 'حُذفت البوّابة.',
    rotated: 'دُوّر المفتاح. نفّذ معاملة اختبارية للتحقّق.',
    activated: (name: string) => `صارت «${name}» البوّابة الفاعلة.`,
  },
} as const;
